// @ts-strict-ignore
import { Injectable } from '@angular/core';
import { ContainerService } from '../container.service';
import { Connection } from '../connection';
import { PropertyVO } from '@project/components/article/model/vo/property.vo';
import { ArticleContainer } from '../article-container';
import { ConnectionAreaVO, MetaElementUsageIdentityVO, SelectionMetaElementUsageIdentity } from '@shared/vo/meta-element.vo';
import { ConnectionInfoUtils } from '../util/connection-info-utils';
import { Mesh, Plane, Vector3 } from 'three';
import { Face3D } from '../../model/face-3d';
import { Rectangle3D } from '../../model/rectangle-3d';
import { MathUtils } from '../../common/util/math-utils';
import { Line } from '../../model/line';
import { PropertyUtils } from '@article/util/property-utils';
import { ArticleUtils } from '@article/util/article-utils';

@Injectable({
  providedIn: 'root'
})
export class ConnectionInfoService {

  constructor(private containerService: ContainerService) {
  }

  /**
   * Create list with feature properties from connection and the article that contains the connection provided by the given connection cube.
   * @param connectionCube connection cube
   * @return array of feature properties
   *
   */
  public findFeaturePropertiesByConnection(connectionCube: Connection): PropertyVO[] {
    const articleContainer: ArticleContainer = this.containerService.getArticleContainer(connectionCube);
    if (!articleContainer || !articleContainer.article || !(connectionCube.metaElementVO instanceof ConnectionAreaVO)) {
      return [];
    }
    return ConnectionInfoUtils.createFeaturePropertyList(connectionCube.metaElementVO, articleContainer.article);
  }

  /**
   * Search faces of article that contain given connection cube.
   * Result contains faces which are on the same plane as connection of giving cube
   *
   * @param connection connection cube
   * @return resulting faces
   *
   */
  public findSidePointsByConnection(connection: Connection, excludedMeshes: Mesh[] = null, epsilon: number = 0.22): Vector3[] {
    const articleContainer: ArticleContainer = connection.articleContainer;
    if (!articleContainer) {
      return [];
    }
    const plane: Plane = new Plane();
    plane.setFromCoplanarPoints(connection.localRectangle.point1, connection.localRectangle.point2, connection.localRectangle.point3);
    const faces: Face3D[] = this.containerService.findFacesOnPlane(articleContainer.model, plane, excludedMeshes, epsilon);
    return ConnectionInfoUtils.getPointsFromFaces(faces, articleContainer);
  }

  /**
   * Calculates rectangle for connecting article
   * Width of rectangle equals size of article in direction of connection from 1st to 4th point of connection rectangle
   * (if inverse set to true, from 1st to 2nd point). Height of rectangle equals size of connection in the same direction.
   *
   * @param connection
   * @param inverse inversion of points of resulting rectangle is needed (article that contains provided cube is giving)
   * @return resulting rectangle
   *
   */
  public calculateRectangleByConnection(connection: Connection, inverse: boolean = false): Rectangle3D {
    const excludedMeshes: Mesh[] = [];
    const sidePoints: Vector3[] = this.findSidePointsByConnection(connection, excludedMeshes);
    const boundingPoints: Vector3[] = MathUtils.calculateBoundingPoints(sidePoints);

    const rect: Rectangle3D = connection.compositionRectangle;
    const farestPoint1: Vector3 = MathUtils.getInterpolated(rect.point1, rect.point3, 100000 / rect.point1.distanceTo(rect.point3));
    const farestPoint2: Vector3 = inverse
      ? MathUtils.getInterpolated(rect.point2, rect.point4, 100000 / rect.point2.distanceTo(rect.point4))
      : MathUtils.getInterpolated(rect.point4, rect.point2, 100000 / rect.point2.distanceTo(rect.point4));

    let point1_1: Vector3;
    let point1_2: Vector3;

    let point2_1: Vector3;
    let point2_2: Vector3;

    for (const point of boundingPoints) {
      if (!point1_1) {
        point1_1 = point1_2 = point;
      }
      if (!point2_1) {
        point2_1 = point2_2 = point;
      } else {
        if (point.distanceTo(farestPoint1) < point1_1.distanceTo(farestPoint1)) {
          point1_1 = point;
        }
        if (point.distanceTo(farestPoint1) > point1_2.distanceTo(farestPoint1)) {
          point1_2 = point;
        }

        if (point.distanceTo(farestPoint2) < point2_1.distanceTo(farestPoint2)) {
          point2_1 = point;
        }
        if (point.distanceTo(farestPoint2) > point2_2.distanceTo(farestPoint2)) {
          point2_2 = point;
        }
      }
    }

    const line1: Line = ConnectionInfoUtils.calculateRectangleSide(point1_1, point2_2, [connection]);
    const line2: Line = ConnectionInfoUtils.calculateRectangleSide(point2_1, point1_2, [connection]);

    const rectangle: Rectangle3D = inverse
      ? Rectangle3D.fromArray([line1.start, line2.start, line2.end, line1.end])
      : Rectangle3D.fromArray([line1.start, line1.end, line2.end, line2.start]);
    return rectangle;
  }

  public findConnectionByIdentity(metaUsageId: MetaElementUsageIdentityVO): Connection {
    if (!metaUsageId) {
      return null;
    }
    let connection: Connection = null;
    const articleContainer: ArticleContainer = this.containerService.findArticleContainerInGlobal(metaUsageId.articleUsageId);
    if (articleContainer) {
      if (metaUsageId instanceof SelectionMetaElementUsageIdentity) {
        connection = articleContainer.findConnection(metaUsageId.metaElementName, metaUsageId.connectionUid);
      } else {
        connection = articleContainer.findFirstConnectionByName(metaUsageId.metaElementName);
      }
    }
    return connection;
  }

  /**
   * Create list with requirement properties from connection and the article that contains the connection provided by the given
   * connection cube.
   * @param connectionCube connection cube
   * @return array of requirement properties
   *
   */
  public findRequirementPropertiesByConnection(connectionCube: Connection): PropertyVO[] {
    const articleContainer: ArticleContainer = this.containerService.getArticleContainer(connectionCube);
    if (!articleContainer || !articleContainer.article || !(connectionCube.metaElementVO instanceof ConnectionAreaVO)) {
      return [];
    }
    return ConnectionInfoUtils.createRequirementPropertyList(connectionCube.metaElementVO, articleContainer.article);
  }

  /**
   * Searches accepting connection in articles on scene that can be connected with the provided giving connection.
   */
  public findRequiredConnectionInGlobal(givingConnection: Connection, excludingArticleGUIDs: string[] = null): Connection {
    if (!givingConnection) {
      return null;
    }
    const containers: ArticleContainer[] = this.containerService.enumerateArticleContainersInGlobalExcept(excludingArticleGUIDs);
    for (const container of containers) {
      const acceptingConnectionAreas: ConnectionAreaVO[] = ConnectionInfoUtils.getFreeConnectionAreas(
        container,
        ConnectionAreaVO.SIDE_ACCEPTING
      );
      for (const acceptingArea of acceptingConnectionAreas) {
        const acceptingConnection: Connection = container.findFirstConnectionByName(acceptingArea.name);
        if (acceptingConnection) {
          const isFittingConnection = this.checkConnectionProperties(givingConnection, acceptingConnection);
          const isFittingPosition = ConnectionInfoUtils.checkConnectionPositions(givingConnection, acceptingConnection);
          if (isFittingConnection && isFittingPosition) {
            return acceptingConnection;
          }
        }
      }
    }

    return null;
  }

  /**
   * Checks the suitability of properties of the giving connection to requirement properties of accepting connection.
   *
   * @param givingConnection giving connection cube
   * @param acceptingConnection accepting connection cube
   * @return true if properties of the giving connection are suitable, otherwise false
   */
  public checkConnectionProperties(givingConnection: Connection, acceptingConnection: Connection): boolean {
    return PropertyUtils.checkProperties(
      this.findFeaturePropertiesByConnection(givingConnection),
      this.findRequirementPropertiesByConnection(acceptingConnection)
    );
  }

  /**
   * @return true if line on the given connection can be drawn, otherwise false
   */
  public isDrawingLinesAvailable(connection: Connection): boolean {
    const articleContainer: ArticleContainer = this.containerService.getArticleContainer(connection);
    if (!articleContainer) {
      return false;
    }
    return ArticleUtils.isDrawingLinesAvailable(articleContainer.article);
  }
}
